import { useMemo } from 'react';
import { useSportEvents } from '../../ses/context/SportEventContext';
import { deriveNbaShifts } from '../../ses/sports/nba/deriveShifts';
import { PlayerShift, PlayerShiftSummary, PlayerStats } from '../types/PlayerShiftTypes';
import { nsToMs } from '../utils/shiftUtils';
import { attributeEventStats } from '../../ses/sports/registry';

/**
 * Aggregates shift events from the SSE stream into per-player summaries.
 *
 * Returns a Map<playerId, PlayerShiftSummary> that recomputes reactively
 * as new events arrive (supports live games).
 *
 * Only includes completed shifts (revision.state === 'UPDATE' with both start and end).
 */
export function usePlayerShifts(): Map<string, PlayerShiftSummary> {
  const { events } = useSportEvents();

  return useMemo(() => {
    const shiftsMap = new Map<string, PlayerShiftSummary>();
    const statsMap = new Map<string, PlayerStats>();
    // Count of stat-contributing attributions per player (value-independent,
    // so weighted stats like NBA points don't inflate the "Events" tally).
    const eventCountMap = new Map<string, number>();

    // NBA has no native `shift` events — SES only sends substitutions. When
    // the stream carries no shift events of its own, synthesize them from the
    // IN/OUT substitution pairs so the rest of this hook is sport-agnostic.
    // (NHL streams real shift events, so derivation is skipped there.)
    const hasNativeShiftEvents = events.some((e) => (e.payload as any)?.type === 'shift');
    const derivedShiftEvents = hasNativeShiftEvents ? [] : deriveNbaShifts(events);
    const shiftSource = derivedShiftEvents.length ? events.concat(derivedShiftEvents) : events;

    // First pass: aggregate stats from all non-shift events. The owning sport
    // module decides which stat (if any) each event credits and by how much
    // (`value`) — this loop stays sport-agnostic.
    for (const event of events) {
      const payload = event.payload as any;
      if (!payload?.type || payload.type === 'shift' || payload.type === 'substitution') continue;

      for (const { playerId, stat, value } of attributeEventStats(event)) {
        let stats = statsMap.get(playerId);
        if (!stats) {
          stats = {};
          statsMap.set(playerId, stats);
        }
        stats[stat] = (stats[stat] ?? 0) + value;
        eventCountMap.set(playerId, (eventCountMap.get(playerId) ?? 0) + 1);
      }
    }

    // Second pass: build shift records from shift events.
    // Dedup by event.id first — during live games SES emits multiple revisions
    // of the same shift (in-progress with end=null, then completed with end set);
    // both can land in `events` if the SSE store doesn't perfectly upsert.
    // We pick the version with `end` populated when available, otherwise the
    // highest revision.version, so a finished shift always wins over its
    // in-progress predecessor.
    const latestByShiftId = new Map<string, typeof events[number]>();
    for (const event of shiftSource) {
      if ((event.payload as any)?.type !== 'shift') continue;
      const key = event.id || event.sourceNativeId;
      if (!key) continue;
      const data = (event.payload as any)?.data;
      const existing = latestByShiftId.get(key);
      if (!existing) {
        latestByShiftId.set(key, event);
        continue;
      }
      const existingHasEnd = (existing.payload as any)?.data?.end != null;
      const newHasEnd = data?.end != null;
      if (newHasEnd && !existingHasEnd) {
        latestByShiftId.set(key, event);
      } else if (newHasEnd === existingHasEnd) {
        const existingVer = existing.revision?.version ?? 0;
        const newVer = event.revision?.version ?? 0;
        if (newVer > existingVer) {
          latestByShiftId.set(key, event);
        }
      }
    }

    for (const event of latestByShiftId.values()) {
      const payload = event.payload as any;

      const shiftData = payload.data;
      if (!shiftData?.player?.id?.main) {
        console.warn('[usePlayerShifts] shift event dropped — missing player.id.main. payload.data:', JSON.stringify(shiftData, null, 2));
        continue;
      }

      const playerId = String(shiftData.player.id.main);
      // A shift is complete iff SES has reported its end timestamp, regardless
      // of the revision state. Some shifts arrive complete in a single
      // INSERT-state event; the prior `state === 'UPDATE'` check excluded them.
      const isCompleted = shiftData.end != null;

      const shift: PlayerShift = {
        shiftId: event.sourceNativeId || event.id || '',
        playbyplayId: event.id ?? '',
        playerId,
        playerName: shiftData.player.name
          ? `${shiftData.player.name.firstname} ${shiftData.player.name.lastname}`
          : 'Unknown',
        jerseyNumber: shiftData.player.jersey || '',
        position: shiftData.player.position || '',
        team: shiftData.player.team === 'home' ? 'home' : 'away',
        teamId: shiftData.player.team_id ? String(shiftData.player.team_id) : '',
        shiftNumber: shiftData.shift_number ?? shiftData.shiftNumber ?? 0,
        period: Number(event.clock?.period ?? event.clock?.time ?? 0),
        gameClockStart: Number(shiftData.game_clock_start ?? shiftData.gameClockStart ?? 0),
        gameClockEnd: Number(shiftData.game_clock_end ?? shiftData.gameClockEnd ?? 0),
        elapsed: shiftData.elapsed ?? 0,
        startTimeMs: nsToMs(shiftData.start),
        endTimeMs: isCompleted ? nsToMs(shiftData.end) : 0,
        isCompleted,
        events: [], // populated by usePlayerShiftDetail
      };

      // Get or create summary
      let summary = shiftsMap.get(playerId);
      if (!summary) {
        summary = {
          playerId,
          playerName: shift.playerName,
          jerseyNumber: shift.jerseyNumber,
          position: shift.position,
          team: shift.team,
          teamId: shift.teamId,
          totalShifts: 0,
          totalTOI: 0,
          totalEvents: 0,
          stats: {},
          shifts: [],
        };
        shiftsMap.set(playerId, summary);
      }

      summary.shifts.push(shift);
      if (isCompleted) {
        summary.totalShifts++;
        summary.totalTOI += shift.elapsed;
      }
    }

    // Merge stats and sort shifts
    for (const [playerId, summary] of shiftsMap) {
      const stats = statsMap.get(playerId);
      if (stats) {
        summary.stats = stats;
        summary.totalEvents = eventCountMap.get(playerId) ?? 0;
      }
      // Sort shifts by period then by game clock start (descending — highest clock = earliest in period)
      summary.shifts.sort((a, b) => {
        if (a.period !== b.period) return a.period - b.period;
        return b.gameClockStart - a.gameClockStart;
      });
    }

    return shiftsMap;
  }, [events]);
}
